import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

const empty = { name: "", role: "cutter", phone: "", active: true };
const ROLES = ["cutter", "stitcher", "packer", "helper", "supervisor", "other"];

const weekKey = (dateStr) => {
  const d = new Date(dateStr);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};

export default function WorkersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [cutting, setCutting] = useState([]);
  const [stitching, setStitching] = useState([]);
  const [packing, setPacking] = useState([]);
  const [tab, setTab] = useState("list");

  const load = () => api.get("/workers").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/cutting").then((r) => setCutting(r.data)).catch(() => {});
    api.get("/stitching").then((r) => setStitching(r.data)).catch(() => {});
    api.get("/packing").then((r) => setPacking(r.data)).catch(() => {});
  }, []);

  const startEdit = (w) => {
    setEditing(w.id);
    setF({ name: w.name, role: w.role || "cutter", phone: w.phone || "", active: w.active !== false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/workers/${editing}`, f);
        toast.success("Worker updated");
      } else {
        await api.post("/workers", f);
        toast.success("Worker added");
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this worker?")) return;
    await api.delete(`/workers/${id}`);
    load();
  };

  const report = useMemo(() => {
    const map = {};
    const bump = (worker, wk, key, delta) => {
      const k = `${worker}|${wk}`;
      if (!map[k]) map[k] = { worker, week: wk, cutting_pcs: 0, stitching_pcs: 0, packing_pcs: 0 };
      map[k][key] += delta;
    };
    cutting.forEach((c) => bump(c.worker_name || "Unassigned", weekKey(c.date), "cutting_pcs", Number(c.pcs_out) || 0));
    stitching.forEach((s) => bump(s.worker_name || "Unassigned", weekKey(s.date), "stitching_pcs", Number(s.pcs) || 0));
    packing.forEach((p) => bump(p.worker_name || "Unassigned", weekKey(p.date), "packing_pcs", Number(p.pcs) || 0));
    return Object.values(map).sort((a, b) => b.week.localeCompare(a.week) || a.worker.localeCompare(b.worker));
  }, [cutting, stitching, packing]);

  return (
    <div>
      <PageHeader label="Master" title="Workers" subtitle="People who cut, stitch, and pack — used in dropdowns and reports" />
      <div className="px-4 md:px-8 pt-4 flex gap-2">
        {["list", "report"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`workers-tab-${t}`}
            className={`h-8 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold ${tab === t ? "bg-foreground text-background" : "bg-white border border-border"}`}>
            {t === "list" ? "Workers" : "Weekly Output"}
          </button>
        ))}
      </div>
      {tab === "list" && (
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <Card title={editing ? "Edit Worker" : "Add Worker"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="worker-form">
            <Field label="Name"><Input data-testid="worker-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Role">
              <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </Select>
            </Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
              <span className="label-caps">Active</span>
            </label>
            <Btn data-testid="worker-submit">{editing ? "Save Changes" : "Add Worker"}</Btn>
          </form>
        </Card>
        <Card title={`Workers · ${list.length}`} className="lg:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Name</th><th>Role</th><th>Phone</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {list.map((w) => (
                  <tr key={w.id} className={editing === w.id ? "bg-secondary" : ""}>
                    <td className="font-semibold">{w.name}</td>
                    <td><span className="label-caps">{w.role || "—"}</span></td>
                    <td className="font-mono">{w.phone || "—"}</td>
                    <td><span className={`label-caps ${w.active ? "text-green-700" : "text-destructive"}`}>{w.active ? "Active" : "Inactive"}</span></td>
                    <td className="text-right whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button onClick={() => startEdit(w)} className="label-caps hover:text-foreground mr-3">Edit</button>
                          <button onClick={() => del(w.id)} className="label-caps hover:text-destructive">Del</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={5}><Empty text="No workers yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      )}
      {tab === "report" && (
        <div className="p-4 md:p-8">
          <Card title={`Weekly Output by Worker · ${report.length} rows`}>
            <div className="table-scroll">
              <table className="table-dense w-full">
                <thead><tr><th>Week</th><th>Worker</th><th className="text-right">Cutting Pcs</th><th className="text-right">Stitching Pcs</th><th className="text-right">Packing Pcs</th></tr></thead>
                <tbody>
                  {report.map((r) => (
                    <tr key={`${r.worker}-${r.week}`}>
                      <td className="font-mono">{r.week}</td>
                      <td className="font-semibold">{r.worker}</td>
                      <td className="tabular text-right">{r.cutting_pcs}</td>
                      <td className="tabular text-right">{r.stitching_pcs}</td>
                      <td className="tabular text-right">{r.packing_pcs}</td>
                    </tr>
                  ))}
                  {report.length === 0 && <tr><td colSpan={5}><Empty text="No output yet — assign workers on Cutting / Stitching / Packing entries" /></td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
